// src/components/personal/sections/ClientsSection/hooks/useClientFilters.ts
import { useState, useCallback, useMemo } from 'react';
import { Client, ClientFilters } from '../types/types';
import { 
  filterClients, 
  isFilteringActive, 
  getEmptyFilters 
} from '../utils/clientFilters';

interface UseClientFiltersProps {
  clients: Client[];
  onFiltersChange?: (filters: ClientFilters) => void;
}

interface UseClientFiltersReturn {
  // Состояние фильтров
  filters: ClientFilters;
  filteredClients: Client[];
  isFiltering: boolean;
  showFilters: boolean;
  
  // Методы управления фильтрами
  updateFilters: (newFilters: Partial<ClientFilters>) => void;
  resetFilters: () => void;
  toggleFilters: () => void;
}

export const useClientFilters = ({
  clients,
  onFiltersChange
}: UseClientFiltersProps): UseClientFiltersReturn => {
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояние фильтров
  const [filters, setFilters] = useState<ClientFilters>(getEmptyFilters());
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Определяем, активны ли фильтры
  const isFiltering = useMemo(() => {
    // Используем searchQuery = '' так как он обрабатывается в useClientSearch
    return isFilteringActive(filters, '');
  }, [filters]);
  
  // Применение фильтров к списку клиентов
  const filteredClients = useMemo(() => {
    if (clients.length === 0) {
      return [];
    }
    
    if (!isFiltering) {
      return clients;
    }
    
    if (DEBUG) console.log(`Фильтруем ${clients.length} клиентов по фильтрам:`, filters);
    
    // Используем searchQuery = '' так как он обрабатывается в useClientSearch
    const result = filterClients(clients, filters, '');
    
    if (DEBUG) console.log(`После фильтрации осталось ${result.length} клиентов`);
    
    return result;
  }, [clients, filters, isFiltering, DEBUG]);
  
  // Метод обновления фильтров
  const updateFilters = useCallback((newFilters: Partial<ClientFilters>) => {
    setFilters(current => {
      const updated = { ...current, ...newFilters };
      
      if (onFiltersChange) {
        onFiltersChange(updated);
      }
      
      return updated;
    });
  }, [onFiltersChange]);
  
  // Сброс фильтров
  const resetFilters = useCallback(() => {
    const emptyFilters = getEmptyFilters();
    setFilters(emptyFilters);
    
    if (onFiltersChange) {
      onFiltersChange(emptyFilters);
    }
  }, [onFiltersChange]);
  
  // Переключение отображения панели фильтров
  const toggleFilters = useCallback(() => {
    setShowFilters(current => !current);
  }, []);
  
  return {
    filters,
    filteredClients,
    isFiltering,
    showFilters,
    updateFilters,
    resetFilters,
    toggleFilters
  };
};